import { KatButton, KatDataTable, KatIcon } from "@amzn/katal-react";
import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import type { NestUseCase } from "src/api/interfaces/nest";
import { useNest } from "src/components/Contexts/NestContext";
import { useDocumentTypePreference } from "../../hooks/useDocumentTypePreference";
import { NestStatusBadge } from "../NestStatusBadge";
import { NestUploadButton } from "../NestUploadButton";
import "./index.css";

interface NestTableProps {
  data: NestUseCase[];
  onUpload: (useCase: NestUseCase, documentTypeId: string) => void;
  onHistory: (useCase: NestUseCase) => void;
  onStatusClick: (useCase: NestUseCase) => void;
}

export function NestTable({
  data,
  onUpload,
  onHistory,
  onStatusClick,
}: NestTableProps) {
  const { t } = useTranslation();
  const { partnerCode } = useNest();
  const { getDocumentType } = useDocumentTypePreference();

  const columns: KatDataTable.ColumnConfiguration[] = [
    { property: "name" },
    { property: "description" },
    {
      property: "status",
      formatter: (_, rowData: any, lastElement: any) => {
        const element = lastElement || document.createElement("div");
        element.style.cursor = "pointer";
        element.onclick = () => onStatusClick(rowData.useCase);
        ReactDOM.render(
          <NestStatusBadge
            status={rowData.status}
            className="nest-table-badge"
          />,
          element
        );
        return { element };
      },
    },
    {
      property: "upload",
      formatter: (_, rowData: any, lastElement: any) => {
        const element = lastElement || document.createElement("div");
        if (!lastElement) {
          element.className = "nest-button-wrapper nest-button-hover";
        }
        ReactDOM.render(
          <NestUploadButton
            onClick={() =>
              onUpload(rowData.useCase, getDocumentType(rowData.useCase).id)
            }
            disabled={!!partnerCode}
            documentTypes={rowData.useCase.documentTypes}
            onSelectDocumentType={(docTypeId) =>
              onUpload(rowData.useCase, docTypeId)
            }
          />,
          element
        );
        return { element };
      },
    },
    {
      property: "history",
      formatter: (_, rowData: any, lastElement: any) => {
        const element = lastElement || document.createElement("div");
        if (!lastElement) {
          element.className = "nest-button-wrapper";
        }
        ReactDOM.render(
          <KatButton
            onClick={() => onHistory(rowData.useCase)}
            variant="tertiary"
            className="nest-history-button"
          >
            <div className="nest-button-content">
              <KatIcon size="small" slot="icon" name="history" />
              {t("nest_button_history")}
            </div>
          </KatButton>,
          element
        );
        return { element };
      },
    },
  ];

  const rowData = data.map((useCase) => ({
    name: t(useCase.name.key, useCase.name.parameters),
    description: t(useCase.description.key, useCase.description.parameters),
    status: useCase.lastRun.status,
    useCase,
  }));

  return (
    <KatDataTable className="nest-table" columns={columns} rowData={rowData} />
  );
}
